import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { UiButtonDirective } from '@template-ui/directives';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  WorkflowDefinitionLifecycleDetail,
  workflowDraftActionLabel,
} from './platform-workflow-definition-lifecycle.models';

type VersionActionsParams =
  ICellRendererParams<WorkflowDefinitionLifecycleDetail> & {
    canAuthor: () => boolean;
    blocked: () => boolean;
    openDefinition: (definition: WorkflowDefinitionLifecycleDetail) => void;
    requestDraft: (definition: WorkflowDefinitionLifecycleDetail) => void;
  };

/** Keep published-version actions on shared controls, including native keyboard activation. */
@Component({
  selector: 'phms-workflow-version-actions-cell',
  imports: [CommonModule, UiButtonDirective],
  host: { role: 'group', 'aria-label': 'Version actions' },
  template: `
    <ng-container *ngIf="definition as version">
      <button
        uiButton
        [text]="true"
        [outlined]="false"
        type="button"
        [attr.aria-label]="
          'Open ' + title + ', version ' + version.definitionVersionCode
        "
        (click)="open()"
      >
        Open version
      </button>
      <button
        *ngIf="showDraftAction"
        uiButton
        type="button"
        [disabled]="blocked"
        [attr.aria-label]="
          draftLabel +
          ' for ' +
          title +
          ', version ' +
          version.definitionVersionCode
        "
        (click)="draft()"
      >
        {{ draftLabel }}
      </button>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        width: 100%;
        min-width: 0;
        --ui-control-min-height: 26px;
        --ui-control-padding-block: 2px;
        --ui-control-font-size: 12px;
      }
      button {
        justify-content: center;
        max-width: 100%;
        white-space: nowrap;
      }
    `,
  ],
})
export class WorkflowVersionActionsCellComponent implements ICellRendererAngularComp {
  definition?: WorkflowDefinitionLifecycleDetail;
  private params?: VersionActionsParams;

  get title(): string {
    return (
      this.definition?.displayName?.trim() ||
      this.definition?.workflowCode ||
      ''
    );
  }

  get showDraftAction(): boolean {
    return Boolean(
      this.params?.canAuthor() &&
      this.definition?.publishedUtc &&
      ['create', 'continue', 'ambiguous'].includes(
        this.definition?.draftActionCode ?? '',
      ),
    );
  }

  get draftLabel(): string {
    return workflowDraftActionLabel(this.definition?.draftActionCode);
  }

  get blocked(): boolean {
    return this.params?.blocked() ?? true;
  }

  agInit(params: VersionActionsParams): void {
    this.params = params;
    this.definition = params.data;
  }

  refresh(params: VersionActionsParams): boolean {
    this.agInit(params);
    return true;
  }

  open(): void {
    if (this.definition) this.params?.openDefinition(this.definition);
  }

  draft(): void {
    if (this.definition && this.showDraftAction && !this.blocked)
      this.params?.requestDraft(this.definition);
  }
}
